// Canonical "Streetname 12" renderer for a residence object.
// Used by Match list cards, the new-match sheet, and the shared
// destination header — keep them in sync here.
export function formatResidenceAddress(residence) {
  if (!residence) return '';
  return [residence.streetAddress, residence.houseNumber]
    .filter(Boolean)
    .join(' ');
}

/**
 * Split a combined "Streetname 12" string into { street, number }.
 *
 * Inverse of formatResidenceAddress. The house number always comes last in
 * SE/DE/NL/DK addresses, so we anchor on the trailing token instead of the first
 * digit — the latter breaks for street names that start with or contain digits
 * (e.g. "1e Helmersstraat 46", "Laan 1940-1945 46", "Straße des 17. Juni 135").
 *
 * Invariant: (street + ' ' + number).trim() reconstructs the input (whitespace-normalized).
 *
 * @param {string} input - e.g. "1e Helmersstraat 46"
 * @returns {{ street: string, number: string }}
 */
export function splitStreetAddress(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return { street: '', number: '' };

  const tokens = trimmed.split(/\s+/);
  if (tokens.length <= 1) return { street: trimmed, number: '' };

  const last = tokens[tokens.length - 1];
  const secondLast = tokens[tokens.length - 2];

  const startsWithDigit = token => /^\d/.test(token);
  const isYearRange = token => /^\d{4}-\d{4}$/.test(token); // "1940-1945" stays in the name

  // Trailing token is the house number: "46", "92A", "12-14".
  if (startsWithDigit(last) && !isYearRange(last)) {
    return { street: tokens.slice(0, -1).join(' '), number: last };
  }

  // House number is "<digits> <single letter>": "Storgatan 12 B".
  if (
    /^[a-zA-Z]$/.test(last) &&
    tokens.length >= 3 &&
    startsWithDigit(secondLast) &&
    !isYearRange(secondLast)
  ) {
    return {
      street: tokens.slice(0, -2).join(' '),
      number: `${secondLast} ${last}`
    };
  }

  // No detectable house number (route-only / year-named square): keep all as street.
  return { street: trimmed, number: '' };
}
